/**
 * Page veil — on departure, a sheet of paper slides over the content before
 * the next page loads. No colour flood.
 *
 * @module chrome/page-veil
 */

import { REDUCED_MOTION } from '../config.js';
import { mount } from '../lib/dom.js';
import { ScrollTrigger, gsap } from '../lib/gsap.js';
import { logoMark } from '../components/icons.js';
import { tuckHeader } from './header.js';

export function renderPageVeil() {
    mount('veil', logoMark('page-veil__mark'));
}

/**
 * Is this click a plain navigation to another page of the site?
 *
 * @param {MouseEvent} event
 * @param {HTMLAnchorElement} link
 * @returns {boolean}
 */
function isInternalNavigation(event, link) {
    const href = link.getAttribute('href') ?? '';

    return !(
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0 ||
        !href ||
        href.startsWith('#') ||
        link.target === '_blank' ||
        link.hasAttribute('download') ||
        /^(mailto|tel|https?):/.test(href)
    );
}

export function enablePageTransitions() {
    const veil = document.querySelector('[data-veil]');

    if (!veil || REDUCED_MOTION) {
        return;
    }

    const mark = veil.querySelector('.page-veil__mark');
    let leaving = false;

    document.addEventListener('click', (event) => {
        const link = event.target instanceof Element ? event.target.closest('a') : null;

        if (leaving || !link || !isInternalNavigation(event, link)) {
            return;
        }

        event.preventDefault();
        leaving = true;

        const href = link.getAttribute('href');

        gsap.timeline()
            .to('#smooth-content', { opacity: 0, duration: 0.35, ease: 'power2.in' }, 0)
            .fromTo(veil, { yPercent: 100 }, { yPercent: 0, duration: 0.55, ease: 'power3.inOut' }, 0)
            .fromTo(mark, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.3 }, 0.25)
            .call(() => {
                window.location.href = href;
            });
    });

    // Back / Forward: the browser restores this page from its cache exactly
    // as it was left — veil up, content faded out, clicks locked. Undo the
    // departure, then remeasure.
    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) {
            return;
        }

        leaving = false;
        gsap.set('#smooth-content', { opacity: 1 });
        gsap.set(veil, { yPercent: 100 });
        gsap.set(mark, { opacity: 0 });
        tuckHeader(false);
        ScrollTrigger.refresh();
    });
}
